import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../overlay/Sheet';
import {
  resolveConflictAcceptServer,
  resolveConflictKeepMine,
  resolveConflictDiscard,
} from '../../lib/offlineQueueDB';

/**
 * A6 Sub-Phase 5 Block 5-MAIN-b — ConflictResolutionDrawer
 *
 * Shown for Tier-3 conflicts (true field conflict + clock drift). Lets the
 * user decide per-conflict which version wins. Tier 1/2 resolve automatically
 * in the sync engine and never reach this UI.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {object} props.conflict - { entryId, conflicting_fields[], current_state{},
 *   client_state{}, client_base{}, drift_detected, drift_seconds }
 * @param {(action: 'server'|'mine'|'discard') => void} props.onResolved
 */
export function ConflictResolutionDrawer({ open, onOpenChange, conflict, onResolved }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!conflict) return null;

  const {
    entryId,
    conflicting_fields = [],
    current_state = {},
    client_state = {},
    drift_detected,
    drift_seconds,
  } = conflict;

  const driftMinutes = drift_seconds != null ? Math.round(Math.abs(drift_seconds) / 60) : null;
  const drawerTitle = t('conflict.title', { defaultValue: 'Änderungskonflikt' }) || 'Änderungskonflikt';

  async function handle(action) {
    if (busy) return;
    setBusy(true);
    try {
      if (action === 'server') {
        await resolveConflictAcceptServer(entryId);
      } else if (action === 'mine') {
        await resolveConflictKeepMine(entryId);
      } else if (action === 'discard') {
        await resolveConflictDiscard(entryId);
      }
      onOpenChange?.(false);
      onResolved?.(action);
    } finally {
      setBusy(false);
    }
  }

  const btnBase = 'px-4 py-2.5 rounded-xl text-sm transition disabled:opacity-50';
  const btnPrimary = `${btnBase} text-white bg-blue-600 hover:bg-blue-700`;
  const btnSecondary = `${btnBase} text-[var(--aif-text-secondary)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-secondary)]`;
  const btnDestructive = `${btnBase} text-red-400 border border-red-400/30 hover:bg-red-400/10`;

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Content
        side="bottom"
        size="sm"
        title={drawerTitle}
      >
        <Sheet.Body>
          <div data-testid="conflict-drawer">
          <p className="text-sm text-[var(--aif-text-secondary)] mb-4">
            {t('conflict.description', {
              defaultValue: 'Diese Daten wurden zwischenzeitlich von jemand anderem geändert. Bitte wähle, welche Version gelten soll.',
            })}
          </p>

          {drift_detected && (
            <div
              className="mb-4 p-3 rounded-lg bg-amber-400/10 border border-amber-400/30 text-amber-300 text-sm"
              role="alert"
              data-testid="conflict-drift-warning"
            >
              {t('conflict.driftWarning', {
                minutes: driftMinutes,
                defaultValue: `Achtung: Die Uhr deines Geräts wich um ${driftMinutes} Minuten ab. Der Zeitvergleich ist daher unsicher — bitte prüfe die Änderungen sorgfältig.`,
              })}
            </div>
          )}

          <div className="space-y-3">
            {conflicting_fields.map((fieldKey) => (
              <div
                key={fieldKey}
                data-testid="conflict-field-row"
                data-field-key={fieldKey}
                className="rounded-lg border border-[var(--aif-border-default)] p-3"
              >
                <div className="text-xs uppercase tracking-wide text-[var(--aif-text-tertiary)] mb-2">
                  {t('conflict.fieldLabel', { defaultValue: 'Feld' })}: {fieldKey}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-[var(--aif-text-tertiary)] mb-1">
                      {t('conflict.serverValue', { defaultValue: 'Andere Version (Server)' })}
                    </div>
                    <div className="font-medium break-words">
                      {String(current_state?.[fieldKey] ?? '—')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--aif-text-tertiary)] mb-1">
                      {t('conflict.myValue', { defaultValue: 'Meine Version' })}
                    </div>
                    <div className="font-medium break-words">
                      {String(client_state?.[fieldKey] ?? '—')}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </Sheet.Body>

        <Sheet.Footer>
          <button
            type="button"
            className={btnDestructive}
            disabled={busy}
            data-testid="conflict-discard"
            onClick={() => handle('discard')}
          >
            {t('conflict.discard', { defaultValue: 'Verwerfen' })}
          </button>
          <button
            type="button"
            className={btnSecondary}
            disabled={busy}
            data-testid="conflict-keep-server"
            onClick={() => handle('server')}
          >
            {t('conflict.acceptServer', { defaultValue: 'Server-Version übernehmen' })}
          </button>
          <button
            type="button"
            className={btnPrimary}
            disabled={busy}
            data-testid="conflict-keep-mine"
            onClick={() => handle('mine')}
          >
            {t('conflict.keepMine', { defaultValue: 'Meine Version behalten' })}
          </button>
        </Sheet.Footer>
      </Sheet.Content>
    </Sheet.Root>
  );
}

export default ConflictResolutionDrawer;
